/*
Timeout Studios Opportunity Assessment
File: opportunity-assessment.js

Purpose:
Native Shopify assessment app.
No database. No PHP. No iframe.
Photos stay local in the browser and are passed to opportunity-pdf.js for report creation.

Requires:
- opportunity-assessment.liquid
- opportunity-assessment.css
- opportunity-pdf.js
*/

(function () {
  const OA = {
    photoData: [],
    data: {
      collections: {
        firstImpressions: {
          title: "First Impressions",
          description: "Exterior and entry-facing branding that shapes the first impression.",
          products: [
            "Exterior signage",
            "Window graphics",
            "Frosted entry graphics",
            "Business hours decals",
            "Accessibility decals",
            "A-frame signs",
            "Exterior banners",
            "Monument or pylon signage"
          ],
          items: [
            "Exterior signage is visible and professional",
            "Main entrance is clearly branded",
            "Window graphics support visibility or promotions",
            "Business hours and contact decals are current",
            "Accessibility decals are present and clear",
            "A-frame or sidewalk signage is being used where appropriate",
            "Exterior banners or temporary promotions are in good condition",
            "Main sign is readable from a reasonable distance"
          ]
        },

        interiorBranding: {
          title: "Interior Branding",
          description: "Branded interior elements that improve the customer or guest experience.",
          products: [
            "Wall murals",
            "Feature wall graphics",
            "Acrylic logo signs",
            "Dimensional lettering",
            "Interior promotional displays",
            "Counter graphics"
          ],
          items: [
            "Interior walls support the brand experience",
            "Reception or front counter area is branded",
            "Feature walls or murals are being used effectively",
            "Acrylic or dimensional logo signage is present where appropriate",
            "Interior graphics are clean, current, and consistent",
            "Promotional displays are professionally presented"
          ]
        },

        wayfinding: {
          title: "Wayfinding",
          description: "Directional signage that helps people move through the space.",
          products: [
            "Directional signage",
            "Department signs",
            "Washroom signs",
            "Elevator signage",
            "Meeting room signage",
            "Floor graphics"
          ],
          items: [
            "Directional signage is clear and easy to follow",
            "Washrooms are clearly identified",
            "Departments, rooms, or service areas are clearly marked",
            "Elevator or corridor signage is consistent",
            "Floor graphics are used where helpful",
            "Wayfinding matches the overall brand style"
          ]
        },

        privacyFilm: {
          title: "Privacy Film",
          description: "Frosted or decorative films that provide privacy while supporting branding.",
          products: [
            "Frosted privacy film",
            "Decorative privacy film",
            "Printed privacy graphics",
            "Office glass film",
            "Meeting room privacy graphics"
          ],
          items: [
            "Glass areas needing privacy have appropriate film",
            "Frosted film is clean and professionally installed",
            "Privacy film supports the brand or interior design",
            "Meeting rooms or office areas have suitable privacy treatment",
            "Glass graphics are not peeling, fading, or damaged"
          ]
        },

        promotionalGraphics: {
          title: "Promotional Graphics",
          description: "Seasonal, campaign, and sales graphics that support marketing goals.",
          products: [
            "Seasonal window campaigns",
            "Posters",
            "POP displays",
            "Table tents",
            "Counter graphics",
            "Promotional decals",
            "Sales banners"
          ],
          items: [
            "Seasonal or campaign graphics are being used",
            "Promotional materials are current and relevant",
            "Window promotions are visible and easy to read",
            "Counter or checkout promotions are professionally displayed",
            "Posters or POP materials are in good condition",
            "Promotional graphics are consistent with the brand"
          ]
        },

        fleetVehicle: {
          title: "Fleet & Vehicle Graphics",
          description: "Vehicle and fleet branding that expands visibility outside the location.",
          products: [
            "Full vehicle wraps",
            "Partial vehicle wraps",
            "Spot graphics",
            "Trailer graphics",
            "Equipment decals",
            "Fleet consistency package",
            "QR code or website decals"
          ],
          items: [
            "Vehicles are branded clearly and professionally",
            "Fleet graphics are consistent across vehicles",
            "Vehicle graphics include website or contact information",
            "Trailer graphics are used where applicable",
            "Equipment decals are branded where useful",
            "Existing vehicle graphics are not faded, peeling, or outdated"
          ]
        },

        safetyCompliance: {
          title: "Safety & Compliance",
          description: "Required and recommended signage that supports safety and accessibility.",
          products: [
            "Accessibility signage",
            "Emergency exit signage",
            "Fire safety signage",
            "Health and safety signage",
            "PPE signage",
            "Floor safety graphics",
            "Compliance decals"
          ],
          items: [
            "Accessibility signage is present and clear",
            "Emergency and exit signage is clear",
            "Health and safety signs are current",
            "Floor safety graphics are used where appropriate",
            "Compliance decals are present where required",
            "Safety signs are professional and consistent"
          ]
        },

        tradeEvents: {
          title: "Trade Show & Events",
          description: "Portable graphics and event materials used for promotions or activations.",
          products: [
            "Pull-up banners",
            "Event backdrops",
            "Table throws",
            "Event signage",
            "Registration graphics",
            "Portable displays"
          ],
          items: [
            "Portable banners or displays are available",
            "Event materials are current and professionally branded",
            "Backdrops or photo areas are available where useful",
            "Table throws or event counters are branded",
            "Event wayfinding or registration signage is available",
            "Trade show materials are easy to transport and reuse"
          ]
        },

        jobSiteProject: {
          title: "Job Site & Project Signage",
          description: "Signs and graphics that identify projects, sites, and active work areas.",
          products: [
            "Coroplast job site signs",
            "ACM construction signs",
            "Fence mesh banners",
            "Development signs",
            "Site directional signs",
            "Project identification signs"
          ],
          items: [
            "Job sites are clearly identified with branded signage",
            "Temporary project signs are professional and readable",
            "Fence banners or mesh graphics are used where appropriate",
            "Directional signs support site access and safety",
            "Development or project signage includes useful contact information",
            "Job site signage is consistent with company branding"
          ]
        },

        officeShowroom: {
          title: "Office & Showroom Branding",
          description: "Contractor-facing office and showroom graphics that support sales and trust.",
          products: [
            "Reception logo wall",
            "Showroom graphics",
            "Product displays",
            "Meeting room branding",
            "Sample boards",
            "Customer presentation graphics"
          ],
          items: [
            "Reception or office entrance is clearly branded",
            "Showroom displays are professional and current",
            "Product or sample displays are easy to understand",
            "Meeting rooms support sales presentations",
            "Customer-facing graphics build trust and credibility",
            "Office branding matches vehicle and field branding"
          ]
        }
      },

      industries: {
        hotels: {
          label: "Hotels & Hospitality",
          collections: [
            "firstImpressions",
            "interiorBranding",
            "wayfinding",
            "privacyFilm",
            "promotionalGraphics",
            "fleetVehicle",
            "safetyCompliance",
            "tradeEvents"
          ]
        },

        restaurants: {
          label: "Restaurants & Foodservice",
          collections: [
            "firstImpressions",
            "interiorBranding",
            "wayfinding",
            "privacyFilm",
            "promotionalGraphics",
            "fleetVehicle",
            "safetyCompliance",
            "tradeEvents"
          ]
        },

        retail: {
          label: "Retail & Storefronts",
          collections: [
            "firstImpressions",
            "interiorBranding",
            "wayfinding",
            "privacyFilm",
            "promotionalGraphics",
            "fleetVehicle",
            "safetyCompliance",
            "tradeEvents"
          ]
        },

        contractors: {
          label: "Contractors & Trades",
          collections: [
            "firstImpressions",
            "promotionalGraphics",
            "fleetVehicle",
            "safetyCompliance",
            "jobSiteProject",
            "officeShowroom"
          ]
        }
      }
    }
  };

  window.TimeoutOpportunityAssessment = OA;

  function qs(selector, context = document) {
    return context.querySelector(selector);
  }

  function qsa(selector, context = document) {
    return Array.from(context.querySelectorAll(selector));
  }

  function val(id) {
    const el = qs("#" + id);
    return el ? String(el.value || "").trim() : "";
  }

  function escapeHtml(str) {
    return String(str || "").replace(/[&<>"']/g, function (m) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;"
      }[m];
    });
  }

  function slug(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function currentIndustryKey() {
    return val("oa-industry");
  }

  function currentIndustry() {
    const key = currentIndustryKey();
    return OA.data.industries[key] || null;
  }

  function activeCollectionKeys() {
    const industry = currentIndustry();
    return industry ? industry.collections : [];
  }

  function renderCategories() {
    const container = qs("#oa-categories");
    if (!container) return;

    const industry = currentIndustry();

    if (!industry) {
      container.innerHTML = `
        <div class="oa-empty-state">
          Select an industry above to load the relevant assessment categories.
        </div>
      `;
      buildSummary();
      return;
    }

    const html = industry.collections.map(function (collectionKey) {
      const collection = OA.data.collections[collectionKey];
      const collectionSlug = slug(collectionKey);

      const itemsHtml = collection.items.map(function (item, index) {
        const itemName = `oa-${collectionSlug}-${index}`;
        return `
          <div class="oa-item" data-collection="${escapeHtml(collectionKey)}" data-label="${escapeHtml(item)}">
            <span class="oa-item-title">${escapeHtml(item)}</span>
            <div class="oa-rating">
              <label>
                <input type="radio" name="${escapeHtml(itemName)}" value="good" data-score="0">
                <span data-score="good">Good</span>
              </label>
              <label>
                <input type="radio" name="${escapeHtml(itemName)}" value="opportunity" data-score="1">
                <span data-score="opportunity">Opportunity</span>
              </label>
              <label>
                <input type="radio" name="${escapeHtml(itemName)}" value="missing" data-score="2">
                <span data-score="missing">Missing</span>
              </label>
            </div>
          </div>
        `;
      }).join("");

      return `
        <article class="oa-category" data-collection-card="${escapeHtml(collectionKey)}">
          <div class="oa-category-header">
            <h3>${escapeHtml(collection.title)}</h3>
            <p>${escapeHtml(collection.description)}</p>
          </div>
          ${itemsHtml}
        </article>
      `;
    }).join("");

    container.innerHTML = html;
    buildSummary();
  }

  function getAssessmentRows() {
    return qsa(".oa-item").map(function (item) {
      const collectionKey = item.getAttribute("data-collection");
      const label = item.getAttribute("data-label");
      const checked = qs("input[type='radio']:checked", item);
      return {
        collectionKey,
        collectionTitle: OA.data.collections[collectionKey] ? OA.data.collections[collectionKey].title : collectionKey,
        label,
        value: checked ? checked.value : "not-assessed",
        score: checked ? Number(checked.getAttribute("data-score") || 0) : 0
      };
    });
  }

  function getCollectionScores() {
    const rows = getAssessmentRows();
    const scores = {};

    activeCollectionKeys().forEach(function (key) {
      scores[key] = {
        key,
        title: OA.data.collections[key].title,
        description: OA.data.collections[key].description,
        products: OA.data.collections[key].products,
        total: 0,
        count: 0,
        opportunityCount: 0,
        missingCount: 0,
        goodCount: 0,
        items: []
      };
    });

    rows.forEach(function (row) {
      if (!scores[row.collectionKey]) return;

      scores[row.collectionKey].count += 1;
      scores[row.collectionKey].total += row.score;
      scores[row.collectionKey].items.push(row);

      if (row.value === "good") scores[row.collectionKey].goodCount += 1;
      if (row.value === "opportunity") scores[row.collectionKey].opportunityCount += 1;
      if (row.value === "missing") scores[row.collectionKey].missingCount += 1;
    });

    return Object.values(scores).sort(function (a, b) {
      const aWeight = a.missingCount * 2 + a.opportunityCount;
      const bWeight = b.missingCount * 2 + b.opportunityCount;
      return bWeight - aWeight;
    });
  }

  function getOpportunityLevel(totalScore) {
    if (totalScore <= 4) {
      return {
        label: "Minor Refresh",
        stars: "★☆☆☆☆",
        range: "Under $2,000",
        description: "The location has a strong baseline with a few minor improvement opportunities."
      };
    }

    if (totalScore <= 10) {
      return {
        label: "Small Project",
        stars: "★★☆☆☆",
        range: "$2,000 to $5,000",
        description: "Several focused improvements could strengthen visibility, consistency, or customer experience."
      };
    }

    if (totalScore <= 18) {
      return {
        label: "Medium Opportunity",
        stars: "★★★☆☆",
        range: "$5,000 to $10,000",
        description: "The site has multiple opportunities across key categories and would benefit from a planned refresh."
      };
    }

    if (totalScore <= 28) {
      return {
        label: "Major Refresh",
        stars: "★★★★☆",
        range: "$10,000 to $30,000",
        description: "The property has a strong opportunity for a larger brand, visibility, or signage refresh."
      };
    }

    return {
      label: "Full Facility Branding Opportunity",
      stars: "★★★★★",
      range: "$30,000+",
      description: "The site shows significant opportunity across multiple collections and could benefit from a complete visual branding plan."
    };
  }

  function getFieldData() {
    const industry = currentIndustry();
    return {
      client: val("oa-client"),
      contact: val("oa-contact"),
      contactEmail: val("oa-contactEmail"),
      contactPhone: val("oa-contactPhone"),
      location: val("oa-location"),
      rep: val("oa-rep"),
      date: val("oa-date") || new Date().toISOString().slice(0, 10),
      industryKey: currentIndustryKey(),
      industryLabel: industry ? industry.label : "",
      goals: val("oa-goals"),
      notes: val("oa-notes")
    };
  }

  function getRecommendedProducts(collectionScores) {
    const products = [];

    collectionScores.forEach(function (collectionScore) {
      const weight = collectionScore.missingCount * 2 + collectionScore.opportunityCount;
      if (weight <= 0) return;

      collectionScore.products.forEach(function (product) {
        products.push({
          collection: collectionScore.title,
          product,
          weight
        });
      });
    });

    const seen = {};
    return products
      .filter(function (item) {
        if (seen[item.product]) return false;
        seen[item.product] = true;
        return true;
      })
      .sort(function (a, b) {
        return b.weight - a.weight;
      })
      .slice(0, 16);
  }

  function buildExecutiveSummary(fields, level, collectionScores) {
    const topCollections = collectionScores.filter(function (collection) {
      return collection.opportunityCount > 0 || collection.missingCount > 0;
    }).slice(0, 3);

    const clientName = fields.client || "This location";
    const industryLabel = fields.industryLabel || "the selected industry";

    if (!topCollections.length) {
      return `${clientName} presents well overall for ${industryLabel}. No major visual branding gaps were identified during this assessment. The recommended next step is to maintain current standards, review seasonal campaigns, and plan future refreshes before materials become dated or worn.`;
    }

    const topNames = topCollections.map(function (item) {
      return item.title;
    }).join(", ");

    return `${clientName} presents a clear opportunity to improve visual impact, customer experience, and brand consistency. The strongest opportunities identified were in ${topNames}. Based on the current assessment, this is best viewed as a ${level.label.toLowerCase()} with a potential investment range of ${level.range}. The recommended next step is to review the priority collections, confirm measurements and materials, then prepare a focused quote.`;
  }

  function buildSummary() {
    const summaryBox = qs("#oa-summary");
    if (!summaryBox) return null;

    const fields = getFieldData();
    const rows = getAssessmentRows();
    const totalScore = rows.reduce(function (sum, row) {
      return sum + row.score;
    }, 0);

    const level = getOpportunityLevel(totalScore);
    const collectionScores = getCollectionScores();
    const recommendedCollections = collectionScores.filter(function (collection) {
      return collection.opportunityCount > 0 || collection.missingCount > 0;
    });

    const recommendedProducts = getRecommendedProducts(collectionScores);
    const executiveSummary = buildExecutiveSummary(fields, level, collectionScores);

    const lines = [];

    lines.push("Timeout Studios Opportunity Assessment");
    lines.push("");
    lines.push("Client:  " + (fields.client || "  [Not entered]"));
    lines.push("Contact:  " + (fields.contact || "  [Not entered]"));
    lines.push("Contact Email:  " + (fields.contactEmail || "  [Not entered]"));
    lines.push("Contact phone:  " + (fields.contactPhone || "  [Not entered]"));
    lines.push("Location:  " + (fields.location || "  [Not entered]"));
    lines.push("Sales Rep:  " + (fields.rep || "  [Not entered]"));
    lines.push("Date:  " + fields.date);
    lines.push("Industry:  " + (fields.industryLabel || "  [Not selected]"));
    lines.push("");
    lines.push("Opportunity Level:  " + level.label);
    lines.push("Opportunity Rating:  " + level.stars);
    lines.push("Potential Investment Range:  " + level.range);
    lines.push("");
    lines.push("Executive Summary:");
    lines.push(executiveSummary);
    lines.push("");

    lines.push("Recommended Collections:");
    if (recommendedCollections.length) {
      recommendedCollections.forEach(function (collection) {
        const weight = collection.missingCount * 2 + collection.opportunityCount;
        lines.push("• " + collection.title + " (" + weight + " opportunity points)");
        lines.push(" ");
      });
    } else {
      lines.push("• No major collection gaps identified.");
    }
    lines.push("");

    lines.push("Recommended Products:");
    if (recommendedProducts.length) {
      recommendedProducts.forEach(function (item) {
        lines.push("• " + item.product + " (" + item.collection + ")");
      });
    } else {
      lines.push("• No product recommendations generated yet.");
    }
    lines.push("");

    lines.push("Goals:");
    lines.push(fields.goals || "[No goals entered]");
    lines.push("");

    lines.push("Notes:");
    lines.push(fields.notes || "[No notes entered]");

    summaryBox.value = lines.join("\n");

    const result = {
      fields,
      rows,
      totalScore,
      level,
      collectionScores,
      recommendedCollections,
      recommendedProducts,
      executiveSummary,
      photoData: OA.photoData
    };

    OA.latestResult = result;
    return result;
  }

function handlePhotoAdd(event) {
  const file = event.target.files && event.target.files[0];
  if (!file) return;

  const reader = new FileReader();

  reader.onload = function (e) {
    const id = "photo-" + Date.now();

    const image = {
      id,
      name: file.name,
      src: e.target.result,
      caption: "",
      observation: "",
      recommendation: ""
    };

    OA.photoData.push(image);
    renderPhotoCards();
    buildSummary();

    event.target.value = "";
  };

  reader.readAsDataURL(file);
}

function renderPhotoCards() {
  const preview = qs("#oa-photo-preview");
  if (!preview) return;

  preview.innerHTML = OA.photoData.map(function (photo, index) {
    return `
      <div class="oa-photo-card" data-photo-id="${escapeHtml(photo.id)}">
        <img src="${photo.src}" alt="${escapeHtml(photo.name)}">

        <div class="oa-photo-fields">
          <strong>Photo ${index + 1}</strong>

          <input
            type="text"
            placeholder="Caption"
            value="${escapeHtml(photo.caption)}"
            data-photo-field="caption"
          >

          <textarea
            placeholder="Observation"
            data-photo-field="observation"
          >${escapeHtml(photo.observation)}</textarea>

          <textarea
            placeholder="Recommendation"
            data-photo-field="recommendation"
          >${escapeHtml(photo.recommendation)}</textarea>

          <button type="button" class="oa-photo-remove">
            Remove Photo
          </button>
        </div>
      </div>
    `;
  }).join("");
}

  function clearForm() {
    const confirmed = window.confirm("Clear the assessment?");
    if (!confirmed) return;

    qsa("input[type='text'], input[type='date'], textarea").forEach(function (el) {
      el.value = "";
    });

    qsa("input[type='radio'], input[type='checkbox']").forEach(function (el) {
      el.checked = false;
    });

    const industry = qs("#oa-industry");
    if (industry) industry.value = "";

    const photos = qs("#oa-photos");
    if (photos) photos.value = "";

    const preview = qs("#oa-photo-preview");
    if (preview) preview.innerHTML = "";

    OA.photoData = [];
    renderCategories();
    buildSummary();
  }

  function createPdf() {
    const result = buildSummary();

    if (!result) return;

    if (window.TimeoutOpportunityPDF && typeof window.TimeoutOpportunityPDF.create === "function") {
      window.TimeoutOpportunityPDF.create(result);
      return;
    }

    alert("PDF builder is missing. Please add opportunity-pdf.js to your Shopify assets.");
  }

function bindEvents() {
  const industry = qs("#oa-industry");
  const photos = qs("#oa-photos");
  const addPhoto = qs("#oa-add-photo");
  const updateSummary = qs("#oa-update-summary");
  const createPdfButton = qs("#oa-create-pdf");
  const clear = qs("#oa-clear");

  if (industry) {
    industry.addEventListener("change", renderCategories);
  }

  if (addPhoto && photos) {
    addPhoto.addEventListener("click", function () {
      photos.click();
    });

    photos.addEventListener("change", handlePhotoAdd);
  }

  if (updateSummary) {
    updateSummary.addEventListener("click", buildSummary);
  }

  if (createPdfButton) {
    createPdfButton.addEventListener("click", createPdf);
  }

  if (clear) {
    clear.addEventListener("click", clearForm);
  }

  document.addEventListener("change", function (event) {
    if (event.target.closest("#timeout-opportunity-assessment")) {
      buildSummary();
    }
  });

  document.addEventListener("input", function (event) {
    const card = event.target.closest(".oa-photo-card");

    if (card) {
      const photoId = card.dataset.photoId;
      const field = event.target.dataset.photoField;

      if (photoId && field) {
        const photo = OA.photoData.find(function (p) {
          return p.id === photoId;
        });

        if (photo) {
          photo[field] = event.target.value;
          buildSummary();
        }
      }

      return;
    }

    if (event.target.closest("#timeout-opportunity-assessment")) {
      buildSummary();
    }
  });

  document.addEventListener("click", function (event) {
    if (!event.target.classList.contains("oa-photo-remove")) return;

    const card = event.target.closest(".oa-photo-card");
    if (!card) return;

    const photoId = card.dataset.photoId;

    OA.photoData = OA.photoData.filter(function (p) {
      return p.id !== photoId;
    });

    renderPhotoCards();
    buildSummary();
  });
}
  function init() {
    const app = qs("#timeout-opportunity-assessment");
    if (!app) return;

    const date = qs("#oa-date");
    if (date && !date.value) {
      date.value = new Date().toISOString().slice(0, 10);
    }

    bindEvents();
    renderCategories();
    buildSummary();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
